import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import RankingTable from "../components/RankingTable";
import Sidebar from "../components/Sidebar";
import EvaluationFramework from "../components/EvaluationFramework";
import FAQSection from "../components/FAQSection";
import FinalCTA from "../components/FinalCTA";
import JsonLd from "../components/JsonLd";
import useSEO from "../hooks/useSEO";
import { platforms, globalFaqs } from "../data/mock";
import { organizationSchema, websiteSchema, faqSchema, itemListSchema } from "../lib/schema";

const sortedPlatforms = [...platforms].sort((a, b) => a.fitRank - b.fitRank);

const Home = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([null, null, null]);

  useSEO({
    title: "Ecommerce Platform Research (2026)",
    description: "Transparent ecommerce platform research with a published 55-criterion methodology, evidence status, platform reviews, comparisons and commercial disclosures.",
    path: "/",
  });

  const handleCompare = () => {
    const slugs = selected.filter(Boolean);
    if (slugs.length < 2) {
      toast.error("Select at least 2 platforms to compare");
      return;
    }
    navigate(`/compare?platforms=${slugs.join(",")}`);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <JsonLd data={organizationSchema()} />
      <JsonLd data={websiteSchema()} />
      <JsonLd data={itemListSchema("Ecommerce Platform Evidence Library", sortedPlatforms.map((platform) => ({ name: platform.name, path: `/${platform.slug}-review` })))} />
      <JsonLd data={faqSchema(globalFaqs)} />
      <Header />
      <main>
        <Hero />
        <section className="mx-auto max-w-[1280px] px-4 py-12 sm:px-6 lg:px-8" data-testid="homepage-methodology-summary">
          <div className="grid gap-8 border-y border-slate-200 py-10 lg:grid-cols-[0.7fr_1.3fr]">
            <div>
              <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-widest text-slate-500">How publication works</p>
              <h2 className="font-serif text-3xl font-bold text-slate-950">A disclosed fit rating—not a hidden universal ranking.</h2>
            </div>
            <div>
              <p className="mb-4 text-sm leading-relaxed text-slate-600">
                The public register contains 550 criterion records across ten platforms. Each record shows whether a relevant first-party source is cited, when it was checked, and whether the capability was directly tested.
              </p>
              <p className="text-sm leading-relaxed text-slate-600">
                Product-quality scores remain withheld. The published 0–10 fit rating measures documented alignment with a declared all-in-one commerce-and-operations profile. Read the <Link to="/methodology" data-testid="homepage-methodology-inline-link" className="font-semibold text-blue-700 underline">formula, weights, ownership conflict and limitations</Link>.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6 lg:px-8" data-testid="homepage-ranking-section">
          <h2 className="font-serif text-3xl font-bold text-slate-950">Integrated commerce fit rating</h2>
          <p className="mb-6 mt-2 max-w-3xl text-sm text-slate-600">Publisher-weighted rating of documented fit for an all-in-one commerce-and-operations profile. It is not an independent product-quality score, user rating or universal best-platform claim.</p>
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="min-w-0 overflow-x-hidden">
              <RankingTable platforms={sortedPlatforms} />
            </div>
            <Sidebar
              selected={selected}
              setSelected={setSelected}
              onCompare={handleCompare}
            />
          </div>
        </section>

        <EvaluationFramework />

        <section id="platform-reviews" className="mx-auto max-w-[1280px] scroll-mt-24 px-4 py-12 sm:px-6 lg:px-8" data-testid="platform-review-library">
          <h2 className="font-serif text-3xl font-bold text-slate-950">Platform review library</h2>
          <p className="mb-6 mt-2 text-sm text-slate-500">Open the complete 55-criterion register, official source links, unverified gaps and source-check record for each platform.</p>
          <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {sortedPlatforms.map((platform) => (
              <Link key={platform.id} to={`/${platform.slug}-review`} data-testid={`platform-review-link-${platform.slug}`} className="group bg-white p-5 transition-colors hover:bg-slate-50">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-bold text-slate-950 group-hover:text-blue-700">{platform.name}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{platform.tagline}</p>
                  </div>
                  <span className="text-right font-mono text-sm font-bold text-slate-900">#{platform.fitRank} · {platform.fitRating}/10 fit<br/><span className="text-[10px] font-medium text-slate-500">{platform.evidenceSummary.cited}/55 cited</span></span>
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-blue-700">Read evidence context →</p>
              </Link>
            ))}
          </div>
        </section>

        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Home;